import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { t } from "@lingui/macro";
import { Plus, Sparkle } from "@phosphor-icons/react";
import { SectionItem, SectionKey, SectionWithItem } from "@reactive-resume/schema";
import { Button, Tooltip } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import get from "lodash.get";
import { useCallback } from "react";

import { AiGenerateSection } from "@/client/components/ai-generate-section";
import { useAiStore } from "@/client/stores/ai";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { ColumnBadge } from "./column-badge";
import { EditableSectionName } from "./editable-name";
import { getSectionIcon } from "./section-icon";
import { SectionListItem } from "./section-list-item";
import { SectionOptions } from "./section-options";

type Props<T extends SectionItem> = {
  id: SectionKey;
  title: (item: T) => string;
  description?: (item: T) => string | undefined;
};

export const SectionBase = <T extends SectionItem>({ id, title, description }: Props<T>) => {
  const { open } = useDialog(id);

  const setValue = useResumeStore((state) => state.setValue);
  const section = useResumeStore((state) =>
    get(state.resume.data.sections, id),
  ) as SectionWithItem<T>;

  const setPanelOpen = useAiStore((state) => state.setPanelOpen);
  const addMessage = useAiStore((state) => state.addMessage);
  const setPendingSend = useAiStore((state) => state.setPendingSend);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onAiImproveSection = useCallback(() => {
    const sectionName = section?.name ?? id;
    const items = (section?.items ?? []) as T[];

    const itemSummaries = items
      .map((item, i) => {
        const parts: string[] = [];
        for (const [k, v] of Object.entries(item)) {
          if (["id", "visible"].includes(k) || !v) continue;
          if (typeof v === "string" && v.trim()) parts.push(`${k}: ${v.trim()}`);
        }
        return `${i + 1}. ${parts.join(" | ")}`;
      })
      .join("\n");

    const fullContext = itemSummaries
      ? `Here is my "${sectionName}" section with ${items.length} item(s):\n\n${itemSummaries}`
      : `I have an empty "${sectionName}" section.`;

    const userPrompt = itemSummaries
      ? `Suggest improvements for my ${sectionName} section. Review each item and give specific, actionable suggestions to improve impact, clarity, and how the content stands out to recruiters.`
      : `I have an empty "${sectionName}" section. What should I add here and how should I structure it?`;

    addMessage({
      id: `ai-section-${Date.now()}`,
      role: "user",
      content: fullContext,
      timestamp: Date.now(),
      type: "context",
      contextLabel:
        items.length === 1
          ? t`${sectionName} section loaded · 1 item`
          : items.length > 1
            ? t`${sectionName} section loaded · ${items.length} items`
            : t`${sectionName} section · empty`,
    });

    setPanelOpen(true);
    setPendingSend(userPrompt);
  }, [id, section, addMessage, setPanelOpen, setPendingSend]);

  if (!section) return null;

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = section.items.findIndex((item) => item.id === active.id);
      const newIndex = section.items.findIndex((item) => item.id === over.id);

      const sortedList = arrayMove(section.items as T[], oldIndex, newIndex);
      setValue(`sections.${id}.items`, sortedList);
    }
  };

  const onCreate = () => open("create", { id });
  const onUpdate = (item: T) => open("update", { id, item });
  const onDuplicate = (item: T) => open("duplicate", { id, item });
  const onDelete = (item: T) => open("delete", { id, item });

  const onToggleVisibility = (index: number) => {
    const visible = get(section, `items[${index}].visible`, true);
    setValue(`sections.${id}.items[${index}].visible`, !visible);
  };

  return (
    <motion.section
      id={id}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="grid gap-y-3"
    >
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-x-2">
          {getSectionIcon(id)}
          <EditableSectionName id={id} name={section.name} />
        </div>

        <div className="flex items-center gap-x-1">
          <ColumnBadge id={id} />
          <SectionOptions id={id} />
        </div>
      </header>

      <main className={cn("grid transition-opacity", !section.visible && "opacity-50")}>
        {section.items.length === 0 && (
          <div className="grid gap-2">
            {id === "experience" ? (
              <button
                type="button"
                onClick={onCreate}
                className="rounded-xl border border-dashed border-primary/30 bg-primary/[0.03] px-4 py-5 text-left transition-colors hover:border-primary/50 hover:bg-primary/[0.06]"
              >
                <p className="text-sm font-semibold">{t`Add your first role`}</p>
                <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                  {t`Company, title, and two or three things you actually did. That's the section recruiters read first.`}
                </p>
              </button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={onCreate}
                  className="gap-x-2 border-dashed py-4 text-xs leading-relaxed hover:bg-secondary-accent"
                >
                  <Plus size={12} />
                  <span className="font-medium">
                    {t({
                      message: "Add a new item",
                      context: "For example, add a new work experience, or add a new profile.",
                    })}
                  </span>
                </Button>
                <AiGenerateSection sectionId={id} />
              </>
            )}
          </div>
        )}

        <DndContext
          sensors={sensors}
          onDragEnd={onDragEnd}
          collisionDetection={closestCenter}
          modifiers={[restrictToParentElement]}
        >
          <SortableContext items={section.items} strategy={verticalListSortingStrategy}>
            <AnimatePresence>
              {section.items.map((item, index) => (
                <SectionListItem
                  id={item.id}
                  key={item.id}
                  visible={item.visible}
                  title={title(item as T)}
                  description={description?.(item as T)}
                  onUpdate={() => onUpdate(item as T)}
                  onDelete={() => onDelete(item as T)}
                  onDuplicate={() => onDuplicate(item as T)}
                  onToggleVisibility={() => onToggleVisibility(index)}
                />
              ))}
            </AnimatePresence>
          </SortableContext>
        </DndContext>
      </main>

      {section.items.length > 0 && (
        <footer className="flex items-center justify-between gap-2">
          <Tooltip content={t`Get AI-powered suggestions to improve this section`}>
            <Button
              size="sm"
              onClick={onAiImproveSection}
              className={cn(
                "group relative gap-x-2 overflow-hidden rounded-lg border-0 px-3 py-1.5 text-xs font-medium",
                "bg-gradient-to-r from-primary/90 via-primary to-primary/90 text-primary-foreground shadow-sm",
                "hover:from-primary hover:via-primary/90 hover:to-primary hover:shadow-md",
                "transition-all duration-200",
              )}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 transition-opacity group-hover:opacity-100" />
              <Sparkle size={13} weight="fill" className="relative z-10 drop-shadow-sm" />
              <span className="relative z-10">{t`AI Improve`}</span>
            </Button>
          </Tooltip>

          <Button variant="outline" size="sm" className="gap-x-1.5 text-xs" onClick={onCreate}>
            <Plus size={12} />
            <span>
              {t({
                message: "Add a new item",
                context: "For example, add a new work experience, or add a new profile.",
              })}
            </span>
          </Button>
        </footer>
      )}
    </motion.section>
  );
};
